import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Currency } from '@/components/common/Currency';
import type { InvoiceLineItemDetail } from '@erp/rpc';
import { T } from 'gt-react';

interface ARInvoiceInvoiceLinesProps {
  invoiceLineItems: InvoiceLineItemDetail[];
  currency?: string | null;
}

export function getLineTypeBadgeVariant(
  lineType: InvoiceLineItemDetail['lineType'],
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (lineType) {
    case 'product':
      return 'default';
    case 'credit_memo':
      return 'destructive';
    case 'company_absorbed':
      return 'outline';
    default:
      return 'secondary';
  }
}

export function formatLineType(lineType: string): string {
  return lineType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function ARInvoiceInvoiceLines({
  invoiceLineItems,
  currency,
}: ARInvoiceInvoiceLinesProps) {
  const currencyCode = currency ?? 'USD';

  if (invoiceLineItems.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        <T context="empty state when AR invoice has no line items">
          No invoice line items
        </T>
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <T context="table column header for AR invoice line item type">
              Type
            </T>
          </TableHead>
          <TableHead>
            <T context="table column header for AR invoice line item description">
              Description
            </T>
          </TableHead>
          <TableHead className="text-right">
            <T context="table column header for AR invoice line item quantity">
              Qty
            </T>
          </TableHead>
          <TableHead className="text-right">
            <T context="table column header for AR invoice line item rate">
              Rate
            </T>
          </TableHead>
          <TableHead className="text-right">
            <T context="table column header for AR invoice line item amount">
              Amount
            </T>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {invoiceLineItems.map((item) => (
          <TableRow key={item.id}>
            <TableCell>
              <Badge variant={getLineTypeBadgeVariant(item.lineType)}>
                {formatLineType(item.lineType)}
              </Badge>
            </TableCell>
            <TableCell className="text-sm">{item.description ?? '—'}</TableCell>
            <TableCell className="text-right text-sm">
              {item.quantity ?? '—'}
            </TableCell>
            <TableCell className="text-right text-sm">
              <Currency
                value={item.rate}
                currency={currencyCode}
                fallback="—"
              />
            </TableCell>
            <TableCell className="text-right text-sm font-bold">
              <Currency value={item.amount} currency={currencyCode} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
